import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['list', 'loading', 'search', 'metadata']
  static values = {
    taskId: String,
    currentValue: String,
    url: String
  }

  connect() {

    this.page = 1
    this.hasMore = true
    this.isLoading = false
    this.searchTerm = ''
    this.hasLoadedOnce = false

    // Get metadata for form building
    if (this.hasMetadataTarget) {
      this.updateUrl = this.metadataTarget.dataset.updateUrl
      this.authenticityToken = this.metadataTarget.dataset.authenticityToken

      // Load users immediately since the frame has already loaded
      if (!this.hasLoadedOnce) {
        this.loadUsers()
        this.hasLoadedOnce = true
      }
    } else {
      console.warn('No metadata target found! Frame might still be loading.')
    }

    // Find the dropdown-menu and attach scroll listener
    this.dropdownMenu = this.element.closest('.dropdown-menu')
    if (this.dropdownMenu) {
      this.boundScrollHandler = this.handleDropdownScroll.bind(this)
      this.dropdownMenu.addEventListener('scroll', this.boundScrollHandler)
    }
  }

  disconnect() {
    // Clean up scroll listener
    if (this.dropdownMenu && this.boundScrollHandler) {
      this.dropdownMenu.removeEventListener('scroll', this.boundScrollHandler)
    }
  }

  handleDropdownScroll(event) {
    const element = event.target
    const isNearBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50

    if (isNearBottom && this.hasMore && !this.isLoading) {
      this.page++
      this.loadUsers()
    }
  }

  filter(event) {
    this.searchTerm = event.target.value.trim()
    this.page = 1
    this.hasMore = true
    this.loadUsers(true)
  }

  scroll(event) {
    const element = event.target
    const isNearBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50

    if (isNearBottom && this.hasMore && !this.isLoading) {
      this.page++
      this.loadUsers()
    }
  }

  async loadUsers(isSearch = false) {
    if (this.isLoading) return

    this.isLoading = true
    if (!isSearch) {
      this.showLoading()
    }

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('page', this.page)
      if (this.currentValueValue) {
        url.searchParams.set('current_value', this.currentValueValue)
      }
      if (this.searchTerm) {
        url.searchParams.set('q', this.searchTerm)
      }

      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      this.hasMore = data.pagination?.more || false

      // Clear list if it's a search or initial load (page 1), otherwise append for pagination
      if (isSearch || this.page === 1) {
        this.clearList()
      }

      this.renderUsers(data.results)
    } catch (error) {
      console.error('Error loading users:', error)
    } finally {
      this.isLoading = false
      this.hideLoading()
    }
  }

  renderUsers(users) {
    users.forEach(user => {
      // Handle separators
      if (user.type === 'separator') {
        const separator = document.createElement('li')
        separator.className = 'list-group-item p-2 bg-light border-top border-bottom position-sticky'
        separator.style.top = '0'
        separator.style.zIndex = '10'
        separator.innerHTML = `<strong class="text-muted small">${user.text}</strong>`
        this.listTarget.appendChild(separator)
        return
      }

      const li = document.createElement('li')
      li.className = 'list-group-item list-group-item-action p-0'

      const isActive = user.is_selected || user.id.toString() === this.currentValueValue?.toString()
      // If user is selected, clicking should unassign (set to empty string)
      const valueToSubmit = isActive ? '' : user.id

      li.innerHTML = `
        <form action="${this.updateUrl}" accept-charset="UTF-8" method="post" data-turbo="true" class="d-block">
          <input type="hidden" name="_method" value="patch" autocomplete="off">
          <input type="hidden" name="authenticity_token" value="${this.authenticityToken}" autocomplete="off">
          <input type="hidden" name="task[assigned_to_id]" value="${valueToSubmit}" autocomplete="off">
          <button type="submit" class="btn btn-link w-100 text-start py-2 text-decoration-none ${isActive ? 'bg-light' : ''}" style="color: inherit; border: none;">
            <div class="d-flex align-items-center justify-content-between">
              <span class="text-dark">${user.text}</span>
              ${isActive ? '<i class="fas fa-check-circle text-primary"></i>' : ''}
            </div>
          </button>
        </form>
      `

      this.listTarget.appendChild(li)
    })

    // Add "loading more" indicator if there are more results
    if (this.hasMore && !this.hasLoadingTarget) {
      const loadingLi = document.createElement('li')
      loadingLi.className = 'p-2 text-center text-muted d-none'
      loadingLi.dataset.assigneeDropdownListTarget = 'loading'
      loadingLi.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Loading more...'
      this.listTarget.appendChild(loadingLi)
    }
  }

  clearList() {
    // Remove all user items (keep data-keep items)
    const userItems = this.listTarget.querySelectorAll('li:not([data-keep])')
    userItems.forEach(item => item.remove())
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('d-none')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('d-none')
    }
  }
}
